import { useState, useEffect, useCallback } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";

import { getMyBookings, completeBooking, cancelBooking, createReview } from "apis";
import { Button, Loading } from "components";
import { useAppModal } from "components/common/AppModalProvider";
import { BookingStatus } from "types";
import { BookingDTO } from "types/DTOs/booking/booking.dto";
import TossCheckout from "components/Payment/TossCheckout";
import PortOneCheckout from "components/Payment/PortOneCheckout";

const PAYMENT_PROVIDER = process.env.REACT_APP_PAYMENT_PROVIDER || "portone";

const statusConfig: Record<string, { label: string; color: string }> = {
  [BookingStatus.PENDING]: { label: "대기", color: "bg-yellow-100 text-yellow-700" },
  [BookingStatus.PAID]: { label: "결제완료", color: "bg-green-100 text-green-700" },
  [BookingStatus.COMPLETED]: { label: "완료", color: "bg-achromatic-100 text-achromatic-500" },
  [BookingStatus.CANCELLED]: { label: "취소", color: "bg-red-100 text-red-500" },
};

interface BookingCardProps {
  booking: BookingDTO;
  onComplete: (id: string) => void;
  onCancel: (id: string) => void;
  onReview: (booking: BookingDTO) => void;
  onPay: (booking: BookingDTO) => void;
  reviewed: boolean;
}

const getTimeUntilSession = (date: string, startTime: string): number => {
  const sessionDateTime = new Date(`${date}T${startTime}`);
  return sessionDateTime.getTime() - Date.now();
};

const formatCountdown = (ms: number): string => {
  if (ms <= 0) return "지금 입장 가능";
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `${hours}시간 ${minutes}분 후 입장`;
  return `${minutes}분 후 입장`;
};

/** 취소 가능 여부 + 사유 */
const getCancelInfo = (booking: BookingDTO): { cancellable: boolean; reason: string } => {
  if (booking.status !== BookingStatus.PAID) return { cancellable: false, reason: '' };
  if (!booking.schedule) return { cancellable: true, reason: '' };

  const now = Date.now();
  const serviceStart = new Date(`${booking.schedule.date}T${booking.schedule.startTime}`).getTime();
  const msUntilStart = serviceStart - now;
  const msSincePaid = booking.paidAt ? now - new Date(booking.paidAt).getTime() : Infinity;
  const GRACE = 10 * 60 * 1000;
  const TWO_HOURS = 2 * 60 * 60 * 1000;

  if (msUntilStart <= 0) return { cancellable: false, reason: '서비스 진행 중' };

  // 일반 예약 (서비스 시작 2시간 이상 전) -> 언제든 취소 가능
  if (!booking.isFinalSale && msUntilStart >= TWO_HOURS) {
    return { cancellable: true, reason: '' };
  }

  // 임박 예약 또는 시작 2시간 이내 예약: 결제 직후 10분 이내일 때만 유예 기간(남은 분) 표시하며 취소 허용
  if (msSincePaid <= GRACE) {
    const remainMin = Math.ceil((GRACE - msSincePaid) / 60000);
    return { cancellable: true, reason: `취소 가능 (${remainMin}분 남음)` };
  }

  if (booking.isFinalSale) return { cancellable: false, reason: '임박예약 취소불가' };
  if (msUntilStart < TWO_HOURS) return { cancellable: false, reason: '2시간 이내 취소불가' };

  return { cancellable: true, reason: '' };
};

const BookingCard = ({ booking, onComplete, onCancel, onReview, onPay, reviewed }: BookingCardProps) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState<string>("");
  const [canEnter, setCanEnter] = useState(false);

  const status = statusConfig[booking.status] ?? statusConfig[BookingStatus.PENDING];

  useEffect(() => {
    if (booking.status !== BookingStatus.PAID || !booking.schedule) return;

    const updateCountdown = () => {
      const msUntilSession = getTimeUntilSession(booking.schedule.date, booking.schedule.startTime);
      const msUntilEntry = msUntilSession - 10 * 60 * 1000;
      setCanEnter(msUntilEntry <= 0);
      setCountdown(formatCountdown(msUntilEntry));
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 30000);
    return () => clearInterval(timer);
  }, [booking]);

  const handleEnterClick = () => {
    if (booking.externalLink) {
      window.open(booking.externalLink, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      className="flex flex-col bg-white rounded-xl border border-achromatic-100 overflow-hidden cursor-pointer hover:border-achromatic-200 hover:shadow-sm transition-all"
      onClick={() => booking.service?.id && navigate(`/service/${booking.service.id}`)}
    >
      <div className="flex justify-between items-start p-4 pb-2">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-black line-clamp-1 flex items-center gap-1.5">
            {booking.service?.title ?? "서비스"}
            {booking.isFinalSale && booking.status === BookingStatus.PAID && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 bg-red-100 text-red-500 rounded-md">🔥 임박</span>
            )}
          </h3>
          <p className="text-xs text-achromatic-400 mt-1">{booking.service?.host?.name ?? "호스트"}</p>
        </div>
        <span className={`text-[10px] font-semibold px-2 py-1 rounded-md ${status.color}`}>{status.label}</span>
      </div>

      <div className="flex items-center gap-4 px-4 py-2 text-xs text-achromatic-500">
        <span>📅 {booking.schedule?.date ?? "-"}</span>
        <span>⏰ {booking.schedule?.startTime?.slice(0, 5) ?? "--:--"} ~ {booking.schedule?.endTime?.slice(0, 5) ?? "--:--"}</span>
        <span className="text-primary-500 font-semibold">
          {booking.paidAmount > 0 ? booking.paidAmount.toLocaleString() : booking.service?.price?.toLocaleString() ?? 0}원
        </span>
      </div>

      {booking.paidAt && (
        <div className="flex items-center gap-3 px-4 py-2 text-[11px] text-achromatic-400 bg-achromatic-50/50">
          <span>💳 {new Date(booking.paidAt).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })} 결제</span>
          {booking.status === BookingStatus.CANCELLED && (
            <>
              <span className="text-red-500 font-semibold">↩ {booking.paidAmount?.toLocaleString()}원 환불</span>
              {booking.cancelledBy === 'host' && booking.cancelReason && (
                <span className="text-achromatic-400">사유: {booking.cancelReason}</span>
              )}
            </>
          )}
          {booking.status === BookingStatus.COMPLETED && (
            <span className="text-green-600 font-semibold">✅ 정산 완료</span>
          )}
        </div>
      )}

      <div className="flex gap-2 px-4 py-3 border-t border-achromatic-50 bg-achromatic-50/30" onClick={e => e.stopPropagation()}>
        {booking.status === BookingStatus.PAID && (
          <>
            <button
              onClick={handleEnterClick}
              disabled={!canEnter}
              className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-colors ${
                canEnter ? "bg-primary-500 text-white hover:bg-primary-600" : "bg-achromatic-100 text-achromatic-300 cursor-not-allowed"
              }`}
            >
              {canEnter ? "🚀 입장하기" : countdown}
            </button>
            <button
              onClick={() => onComplete(booking.id)}
              disabled={booking.completedByGuest}
              className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                booking.completedByGuest ? "bg-achromatic-100 text-achromatic-300" : "bg-green-50 text-green-600 hover:bg-green-100"
              }`}
            >
              {booking.completedByGuest ? "완료됨" : "완료 확인"}
            </button>
            {(() => {
              const cancelInfo = getCancelInfo(booking);
              return (
                <button
                  onClick={() => cancelInfo.cancellable && onCancel(booking.id)}
                  disabled={!cancelInfo.cancellable}
                  title={cancelInfo.reason}
                  className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                    cancelInfo.cancellable ? "text-red-500 bg-red-50 hover:bg-red-100" : "text-achromatic-300 bg-achromatic-50 cursor-not-allowed"
                  }`}
                >
                  {cancelInfo.cancellable ? (cancelInfo.reason || '취소') : cancelInfo.reason}
                </button>
              );
            })()}
          </>
        )}
        {booking.status === BookingStatus.COMPLETED && (
          <div className="flex items-center gap-2 flex-1">
            <span className="text-xs text-achromatic-400 py-2">✅ 서비스가 완료되었습니다</span>
            {!reviewed && (
              <button onClick={() => onReview(booking)} className="ml-auto px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors">⭐ 리뷰 작성</button>
            )}
            {reviewed && <span className="ml-auto text-[11px] text-achromatic-400">리뷰 완료</span>}
          </div>
        )}
        {booking.status === BookingStatus.CANCELLED && (
          <span className="text-xs text-achromatic-400 py-2">❌ 예약이 취소되었습니다</span>
        )}
        {booking.status === BookingStatus.PENDING && (
          <button
            onClick={() => onPay(booking)}
            className="flex-1 py-2.5 rounded-lg text-xs font-bold bg-blue-500 text-white hover:bg-blue-600 transition-colors"
          >
            💳 결제하기
          </button>
        )}
      </div>
    </div>
  );
};

const BookingListComponent = () => {
  const queryClient = useQueryClient();
  const { showAlert, showConfirm } = useAppModal();
  const [reviewBooking, setReviewBooking] = useState<BookingDTO | null>(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewedIds, setReviewedIds] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [payingBooking, setPayingBooking] = useState<BookingDTO | null>(null);

  const { data: bookingsData, isLoading, isError } = useQuery("myBookings", getMyBookings);
  const bookings: BookingDTO[] = bookingsData?.data?.data ?? [];

  const handleComplete = useCallback(async (bookingId: string) => {
    try {
      await completeBooking(bookingId);
      queryClient.invalidateQueries("myBookings");
    } catch (error) {
      console.error("Complete failed:", error);
      showAlert("완료 처리 중 오류가 발생했습니다.", { variant: 'error' });
    }
  }, [queryClient]);

  const handleCancel = useCallback(async (bookingId: string) => {
    if (!(await showConfirm("예약을 취소하시겠습니까?", { title: '예약 취소', confirmText: '취소하기', variant: 'warning' }))) return;
    try {
      await cancelBooking(bookingId);
      queryClient.invalidateQueries("myBookings");
    } catch (error) {
      console.error("Cancel failed:", error);
      showAlert("취소 처리 중 오류가 발생했습니다.", { variant: 'error' });
    }
  }, [queryClient]);

  const handlePay = (booking: BookingDTO) => {
    setPayingBooking(booking);
  };

  const handleSubmitReview = async () => {
    if (!reviewBooking) return;
    setIsSubmitting(true);
    try {
      await createReview(reviewRating, reviewComment, reviewBooking.id);
      setReviewedIds(prev => new Set(prev).add(reviewBooking.id));
      setReviewBooking(null);
      setReviewRating(5);
      setReviewComment("");
      showAlert("리뷰가 등록되었습니다!", { variant: 'success', icon: '⭐' });
    } catch (error) {
      console.error("Review failed:", error);
      showAlert("리뷰 등록 중 오류가 발생했습니다.", { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <Loading />;
  if (isError) return <div className="text-center py-10 text-achromatic-400">예약 내역을 불러오는 중 오류가 발생했습니다.</div>;

  if (bookings.length === 0) {
    return (
      <div className="flex flex-col items-center py-16 gap-3 text-achromatic-300">
        <div className="text-4xl">📋</div>
        <p className="font-medium">예약 내역이 없습니다</p>
        <p className="text-sm">서비스를 예약하면 여기에 표시됩니다.</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-3 p-5">
        {bookings.map((booking) => (
          <BookingCard
            key={booking.id}
            booking={booking}
            onComplete={handleComplete}
            onCancel={handleCancel}
            onReview={(b) => { setReviewBooking(b); setReviewRating(5); setReviewComment(""); }}
            onPay={handlePay}
            reviewed={booking.hasReview || reviewedIds.has(booking.id)}
          />
        ))}
      </div>

      {payingBooking && (
        PAYMENT_PROVIDER === "portone" ? (
          <PortOneCheckout
            bookingId={payingBooking.id}
            amount={payingBooking.service?.price ?? 0}
            orderName={payingBooking.service?.title ?? "서비스 예약"}
            customerName={payingBooking.guest?.name ?? "게스트"}
            onClose={() => setPayingBooking(null)}
          />
        ) : (
          <TossCheckout
            bookingId={payingBooking.id}
            amount={payingBooking.service?.price ?? 0}
            orderName={payingBooking.service?.title ?? "서비스 예약"}
            customerName={payingBooking.guest?.name ?? "게스트"}
            onClose={() => setPayingBooking(null)}
          />
        )
      )}

      {reviewBooking && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setReviewBooking(null)}>
          <div className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-achromatic-900 mb-1">리뷰 작성</h3>
            <p className="text-sm text-achromatic-400 mb-4">{reviewBooking.service?.title} · {reviewBooking.service?.host?.name}</p>
            <div className="flex gap-2 justify-center mb-4">
              {[1, 2, 3, 4, 5].map(star => (
                <button key={star} onClick={() => setReviewRating(star)}
                  className={`text-3xl transition-transform ${star <= reviewRating ? 'scale-110' : 'opacity-30 hover:opacity-60'}`}>⭐</button>
              ))}
            </div>
            <div className="text-center text-sm font-bold text-amber-600 mb-4">
              {reviewRating === 5 ? '아주 좋아요!' : reviewRating === 4 ? '좋아요' : reviewRating === 3 ? '보통이에요' : reviewRating === 2 ? '별로예요' : '안 좋았어요'}
            </div>
            <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)} placeholder="호스트에 대한 솔직한 리뷰를 작성해주세요..."
              className="w-full px-4 py-3 rounded-xl border border-achromatic-200 text-sm focus:border-primary-500 focus:outline-none resize-none min-h-[100px] mb-4" />
            <div className="flex gap-2">
              <button onClick={() => setReviewBooking(null)}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-achromatic-500 bg-achromatic-100 hover:bg-achromatic-200 transition-colors">취소</button>
              <button onClick={handleSubmitReview} disabled={isSubmitting}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-white bg-primary-500 hover:bg-primary-600 transition-colors disabled:opacity-50">
                {isSubmitting ? '등록 중...' : '리뷰 등록'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BookingListComponent;
